/**
 * MainView — primary upload & review workflow
 *
 * 1. Upload PDF  →  pipeline job created
 * 2. Auto-scan   →  each page classified (chart / text) + Takeoff/Landing + Section
 * 3. User review →  toggle any page
 * 4. Confirm     →  batch folders created, panel detection queued
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useJobStore } from "../../stores/jobStore";
import { api, JobListItem, JobPageOut } from "../../api/client";

// ── helpers ──────────────────────────────────────────────────────────────────

const STATUS_DOT: Record<string, string> = {
  QUEUED:               "bg-slate-500",
  RUNNING:              "bg-blue-500 animate-pulse",
  INTAKE_COMPLETE:      "bg-cyan-500",
  CATEGORIZED:          "bg-emerald-500",
  NEEDS_HUMAN_APPROVAL: "bg-amber-500 animate-pulse",
  RESUMING:             "bg-violet-500 animate-pulse",
  COMPLETED:            "bg-emerald-500",
  FAILED:               "bg-red-500",
};

// ── Sidebar: job list ─────────────────────────────────────────────────────────

function JobSidebar({
  jobs,
  loading,
  selectedId,
  onSelect,
  onNewJob,
}: {
  jobs: JobListItem[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNewJob: () => void;
}) {
  return (
    <div className="w-60 flex-shrink-0 flex flex-col border-r border-rdp-border">
      <div className="flex items-center justify-between px-3 py-3 border-b border-rdp-border">
        <span className="text-xs font-semibold text-white">My PDFs</span>
        <button
          onClick={onNewJob}
          className="w-6 h-6 rounded-md bg-blue-600 hover:bg-blue-500 flex items-center justify-center cursor-pointer transition-colors"
          title="Upload new PDF"
        >
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {loading && !jobs.length ? (
          <div className="flex items-center justify-center h-20">
            <div className="w-4 h-4 rounded-full border-2 border-rdp-border border-t-blue-500 animate-spin" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-20 text-slate-600 gap-1">
            <p className="text-xs">No PDFs yet</p>
            <button onClick={onNewJob} className="text-[10px] text-blue-400 hover:text-blue-300 underline cursor-pointer">
              Upload first
            </button>
          </div>
        ) : (
          jobs.map((j) => (
            <button
              key={j.id}
              onClick={() => onSelect(String(j.id))}
              className={[
                "w-full text-left px-2.5 py-2 rounded-lg border transition-all duration-150 cursor-pointer",
                selectedId === String(j.id)
                  ? "bg-rdp-active border-blue-600/60 shadow-glow-blue"
                  : "bg-rdp-card border-rdp-border hover:bg-rdp-hover hover:border-rdp-hover",
              ].join(" ")}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT[j.status] ?? "bg-slate-500"}`} />
                <span className="text-xs text-white truncate">{j.source_filename}</span>
              </div>
              <p className="text-[10px] text-slate-600 font-mono mt-0.5 ml-3">
                {j.chart_count} charts
              </p>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// ── Upload modal ──────────────────────────────────────────────────────────────

function UploadModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { uploadJob } = useJobStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const handle = async (file: File) => {
    if (!file.name.toLowerCase().endsWith(".pdf")) { setError("Only PDF files."); return; }
    setError(null); setUploading(true); setProgress(0);
    try {
      await uploadJob(file, (p) => setProgress(p));
      // store already called selectJob internally — just close modal
      onDone();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Upload failed");
      setUploading(false);
    }
  };

  return (
    <div className="absolute inset-0 z-30 bg-rdp-bg/80 backdrop-blur-sm flex items-center justify-center p-8">
      <div className="w-full max-w-sm bg-rdp-card border border-rdp-border rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-rdp-border">
          <p className="text-sm font-semibold text-white">Upload PDF</p>
          {!uploading && (
            <button onClick={onClose} className="text-slate-500 hover:text-white cursor-pointer text-lg leading-none">✕</button>
          )}
        </div>
        <div className="p-5">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) handle(f); }}
            onClick={() => !uploading && inputRef.current?.click()}
            className={[
              "w-full rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-3 py-10 select-none transition-all",
              dragging ? "border-blue-400 bg-blue-500/10 cursor-copy" :
              uploading ? "border-rdp-border bg-rdp-card/30 cursor-default" :
              "border-rdp-border hover:border-slate-500 bg-rdp-card/20 hover:bg-rdp-card/40 cursor-pointer",
            ].join(" ")}
          >
            <input ref={inputRef} type="file" accept=".pdf" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handle(f); e.target.value = ""; }} />

            {uploading ? (
              <>
                <div className="w-7 h-7 rounded-full border-2 border-rdp-border border-t-blue-500 animate-spin" />
                <p className="text-xs text-white">Uploading… <span className="text-blue-400 font-mono">{progress}%</span></p>
                <div className="w-36 h-1 bg-rdp-border rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
              </>
            ) : (
              <>
                <svg className="w-7 h-7 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0121 9.414V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm text-slate-400">Drop PDF or <span className="text-blue-400">browse</span></p>
              </>
            )}
          </div>
          {error && <p className="mt-2 text-xs text-red-400 px-1">{error}</p>}
        </div>
      </div>
    </div>
  );
}

// ── Page thumbnail grid ───────────────────────────────────────────────────────

function PageGrid({
  pages,
  jobId,
  onToggle,
}: {
  pages: JobPageOut[];
  jobId: string;
  onToggle: (pageNum: number) => void;
}) {
  return (
    <div className="grid grid-cols-4 gap-2 xl:grid-cols-5 2xl:grid-cols-6 p-3">
      {pages.map((page) => (
        <button
          key={page.page_num}
          onClick={() => onToggle(page.page_num)}
          title={`Page ${page.page_num + 1} — click to toggle`}
          className={[
            "relative group rounded-lg overflow-hidden border-2 transition-all duration-150 cursor-pointer",
            page.is_chart
              ? "border-emerald-500 shadow-emerald-500/20 shadow-md"
              : "border-rdp-border hover:border-slate-500",
          ].join(" ")}
        >
          <img
            src={api.jobs.pageThumbnailUrl(jobId, page.page_num)}
            alt={`Page ${page.page_num + 1}`}
            className="w-full aspect-[3/4] object-cover bg-slate-800"
            loading="lazy"
          />

          {/* Page number */}
          <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-black/60 text-[9px] text-slate-400 text-center">
            {page.page_num + 1}
          </div>

          {/* Chart / Text badge */}
          <div className={[
            "absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shadow",
            page.is_chart ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-400 group-hover:bg-slate-600",
          ].join(" ")}>
            {page.is_chart ? "✓" : "✗"}
          </div>

          {/* Category badge */}
          {page.is_chart && page.chart_category && page.chart_category !== "unknown" && (
            <div className={[
              "absolute top-1 left-1 px-1 py-0.5 rounded text-[8px] font-bold",
              page.chart_category === "landing" ? "bg-emerald-600/80 text-white" : "bg-sky-600/80 text-white",
            ].join(" ")}>
              {page.chart_category === "landing" ? "L" : "T"}
            </div>
          )}

          {/* Section badge */}
          {page.is_chart && page.session_id && (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center">
              <span className="text-[8px] bg-black/70 text-slate-300 px-1 rounded font-mono">
                §{page.session_id}
              </span>
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

// ── Categorize result tree ────────────────────────────────────────────────────

function CategorizeResult({
  tree,
  folders,
  onOpenBatchCrop,
}: {
  tree: Record<string, Record<string, number[]>>;
  folders: string[];
  onOpenBatchCrop: () => void;
}) {
  return (
    <div className="flex-shrink-0 px-5 py-4 border-b border-rdp-border bg-emerald-900/10">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-emerald-400">
          Organized into Batch Crop — panel detection queued
        </p>
        <button
          onClick={onOpenBatchCrop}
          className="text-xs text-blue-400 hover:text-blue-300 font-medium px-3 py-1 rounded-lg border border-blue-500/30 hover:bg-blue-500/10 transition-all cursor-pointer"
        >
          Open Batch Crop →
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {folders.map((f) => (
          <span key={f} className="text-[11px] font-mono bg-rdp-card border border-rdp-border text-slate-300 px-2 py-1 rounded-lg">
            📁 {f}
          </span>
        ))}
      </div>
      <div className="mt-3 space-y-1">
        {Object.entries(tree).map(([cat, sessions]) => (
          <div key={cat} className="ml-1">
            <span className="text-[11px] font-semibold text-slate-300">
              {cat === "takeoff" ? "✈️" : cat === "landing" ? "🛬" : "📁"} {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </span>
            {Object.entries(sessions).map(([ses, pages]) => (
              <div key={ses} className="ml-4 text-[10px] text-slate-500">
                § {ses} — {pages.length} page{pages.length !== 1 ? "s" : ""}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main panel: scan progress + page grid + confirm ───────────────────────────

function JobPanel({
  onOpenBatchCrop,
}: {
  onOpenBatchCrop: () => void;
}) {
  const {
    selectedJob, pages, pagesLoading, intakeProgress,
    togglePage, categorizeJob, categorizing,
    categorizeTree, categorizeFolders,
  } = useJobStore();

  if (!selectedJob) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-600">
        <svg className="w-12 h-12 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0121 9.414V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-sm">Select a PDF or upload a new one</p>
      </div>
    );
  }

  const chartCount = pages.filter((p) => p.is_chart).length;
  const canCategorize = (selectedJob.status === "INTAKE_COMPLETE" || selectedJob.status === "CATEGORIZED") && chartCount > 0;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-rdp-border bg-rdp-card/30">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">{selectedJob.source_filename}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            {pages.length} pages scanned · <span className="text-emerald-400 font-semibold">{chartCount}</span> marked as chart
          </p>
        </div>

        {canCategorize && (
          <button
            onClick={() => categorizeJob(String(selectedJob.id))}
            disabled={categorizing}
            className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors cursor-pointer"
          >
            {categorizing ? (
              <>
                <span className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                Organizing…
              </>
            ) : (
              <>✓ Confirm Chart Pages</>
            )}
          </button>
        )}
      </div>

      {/* Scan progress */}
      {intakeProgress && (
        <div className="flex-shrink-0 px-5 py-3 border-b border-rdp-border bg-rdp-bg/60">
          <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1.5">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse inline-block" />
              Scanning page {intakeProgress.scanned} of {intakeProgress.total}
            </span>
            <span className="font-mono text-blue-400 font-semibold">
              {intakeProgress.chartsFound} charts found
            </span>
          </div>
          <div className="h-1.5 bg-rdp-border rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all duration-500"
              style={{ width: `${Math.round((intakeProgress.scanned / intakeProgress.total) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Categorize result */}
      {categorizeTree && categorizeFolders && (
        <CategorizeResult
          tree={categorizeTree}
          folders={categorizeFolders}
          onOpenBatchCrop={onOpenBatchCrop}
        />
      )}

      {/* Page grid */}
      <div className="flex-1 overflow-y-auto">
        {pagesLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-5 h-5 rounded-full border-2 border-rdp-border border-t-blue-500 animate-spin" />
          </div>
        ) : pages.length === 0 && !intakeProgress ? (
          <div className="flex items-center justify-center h-32 text-slate-600 text-sm">
            {selectedJob.status === "QUEUED" || selectedJob.status === "RUNNING"
              ? "Scanning in progress…"
              : "No pages found"}
          </div>
        ) : (
          <PageGrid
            pages={pages}
            jobId={String(selectedJob.id)}
            onToggle={(pn) => togglePage(String(selectedJob.id), pn)}
          />
        )}
      </div>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────

interface MainViewProps {
  onOpenBatchCrop: () => void;
}

export function MainView({ onOpenBatchCrop }: MainViewProps) {
  const { jobs, jobsLoading, loadJobs, selectJob, selectedJob } = useJobStore();
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const handleUploaded = useCallback(() => {
    setShowUpload(false);
    loadJobs();
  }, [loadJobs]);

  return (
    <div className="relative flex flex-1 h-full overflow-hidden">
      <JobSidebar
        jobs={jobs}
        loading={jobsLoading}
        selectedId={selectedJob ? String(selectedJob.id) : null}
        onSelect={(id) => selectJob(id)}
        onNewJob={() => setShowUpload(true)}
      />

      <JobPanel onOpenBatchCrop={onOpenBatchCrop} />

      {showUpload && (
        <UploadModal onClose={() => setShowUpload(false)} onDone={handleUploaded} />
      )}

    </div>
  );
}
